import { useCallback, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";

export const Lightbox = ({ images, index, onClose, onNavigate }) => {
  const touchStartX = useRef(null);
  const count = images.length;

  const prev = useCallback(() => onNavigate((index - 1 + count) % count), [index, count, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % count), [index, count, onNavigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx > 0 ? prev : next)();
    touchStartX.current = null;
  };

  return (
    <motion.div
      data-testid="lightbox-overlay"
      className="fixed inset-0 z-[100] bg-paper/[0.97] backdrop-blur-sm flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between px-6 md:px-10 py-5" onClick={(e) => e.stopPropagation()}>
        <p data-testid="lightbox-counter" className="text-[11px] uppercase tracking-[0.3em] text-smoke">
          {index + 1} / {count}
        </p>
        <button
          data-testid="lightbox-close"
          onClick={onClose}
          aria-label="Close lightbox"
          className="p-2 text-ink hover:text-clay transition-colors"
        >
          <X size={22} strokeWidth={1.25} />
        </button>
      </div>

      <div className="relative flex-1 flex items-center justify-center px-6 md:px-24 pb-8 min-h-0">
        <motion.img
          key={index}
          data-testid="lightbox-image"
          src={images[index]}
          alt={`View ${index + 1} of ${count}`}
          className="max-h-full max-w-full object-contain"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />

        {count > 1 && (
          <>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              aria-label="Previous image"
              className="absolute left-3 md:left-10 top-1/2 -translate-y-1/2 p-3 text-ink/60 hover:text-ink transition-colors"
            >
              <ArrowLeft size={26} strokeWidth={1.25} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              aria-label="Next image"
              className="absolute right-3 md:right-10 top-1/2 -translate-y-1/2 p-3 text-ink/60 hover:text-ink transition-colors"
            >
              <ArrowRight size={26} strokeWidth={1.25} />
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
};
